import React, { useState, useEffect, useRef } from 'react';
import './IndividualTab.css';
import ClearIcon from '@mui/icons-material/Clear';
import { Tooltip } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LuMoveHorizontal } from 'react-icons/lu';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';

interface IndividualTabProps {
  tabName: string;
  totalTabs: number;
  tabIndex: number;
  editing: boolean;
  selectedTab: number;
  backgroundColor?: any;
  tabId: number;
  editingTabId: number | null;
  showDash: boolean;
  dashMode: string;
  popupClass?: string;
  inPopup?: boolean;
  tabAreaBackgroundColor?: string;
  selectedTabBackgroundColor?: string;
  iconColor?: string;
  userRole?: 'developer' | 'user';
  isEditable?: boolean;
  hidden?: boolean;
  visibleTabsCount: number;
  toggleTabHidden: (tabId: number) => void;

  selectTab: (tabName: string, tabId: number) => void;
  removeTab: (tabName: string, tabId: number) => void;
  renameTabBegin: (tabId: number) => void;
  renameTabComplete: (renameValue: string, tabId: number) => void;
  reOrderTabList: (moveAction: string, tabIndex: number) => void;
  otherTabNames?: string[];
}

function IndividualTab({
  tabName,
  totalTabs,
  tabIndex,
  editing,
  selectedTab,
  backgroundColor,
  tabId,
  showDash,
  editingTabId,
  dashMode,
  popupClass,
  inPopup = false,
  tabAreaBackgroundColor,
  iconColor,
  selectedTabBackgroundColor,
  userRole = 'developer',
  isEditable = true,
  hidden = false,
  visibleTabsCount,
  toggleTabHidden,

  selectTab,
  removeTab,
  renameTabBegin,
  renameTabComplete,
  reOrderTabList,
  otherTabNames = [],
}: IndividualTabProps) {
  const [renameValue, setRenameValue] = useState<string>(tabName);
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | any>(null);
  const open: boolean = Boolean(anchorEl);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | any>(null);
  const openSubMenu: boolean = Boolean(subMenuAnchorEl);
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const handleSubmenuClose = (e?: any) => {
    e?.stopPropagation?.();
    setSubMenuAnchorEl(null);
    setActiveItem(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (e?: any) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleConfirmDelete = (e: any) => {
    e.stopPropagation();
    removeTab(tabName, tabId);
    setShowDeleteConfirmation(false);
  };

  const handleCancelDelete = (e: any) => {
    e.stopPropagation();
    setShowDeleteConfirmation(false);
  };

  const handleDeleteClick = (e: any) => {
    e.stopPropagation();
    handleCloseMenu();
    setShowDeleteConfirmation(true);
  };

  // Helper function to check if tab actions are allowed
  const canPerformActions = () => {
    if (userRole === 'developer') return true;
    if (userRole === 'user') return isEditable;
    return false;
  };

  interface SubMenuOption {
    label: string;
    icon: JSX.Element;
    action: 'left' | 'right' | 'first' | 'last';
  }

  const getSubMenuOptions = (index: number, total: number): JSX.Element[] => {
    let options: SubMenuOption[] = [];

    if (index === 0) {
      options = [
        {
          label: 'To Right',
          icon: <ArrowForwardIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'right',
        },
        {
          label: 'To Last',
          icon: <LastPageIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'last',
        },
      ];
    } else if (index === total - 1) {
      options = [
        {
          label: 'To Left',
          icon: <ArrowBackIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'left',
        },
        {
          label: 'To First',
          icon: <FirstPageIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'first',
        },
      ];
    } else {
      options = [
        {
          label: 'To Left',
          icon: <ArrowBackIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'left',
        },
        {
          label: 'To Right',
          icon: <ArrowForwardIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'right',
        },
        {
          label: 'To First',
          icon: <FirstPageIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'first',
        },
        {
          label: 'To Last',
          icon: <LastPageIcon style={{ width: 18, height: 18, color: iconColor }} />,
          action: 'last',
        },
      ];
    }

    return options.map((option, ind) => (
      <MenuItem
        key={ind}
        onClick={(e: any) => {
          e.stopPropagation();
          reOrderTabList(option.action, tabIndex);
          handleCloseMenu();
          handleSubmenuClose();
        }}
      >
        <ListItemIcon>{option.icon}</ListItemIcon>
        <ListItemText primary={option.label} primaryTypographyProps={{ color: iconColor }} />
      </MenuItem>
    ));
  };

  useEffect(() => {
    setRenameValue(tabName);
  }, [tabName]);

  useEffect(() => {
    return () => {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
    };
  }, []);

  const handleTabNameValue = (e: any) => {
    setRenameValue(e.target.value);
  };

  const isTooLong = renameValue.length > 40;
  const isDuplicate = otherTabNames.some(
    (name) => name.toLowerCase() === renameValue.trim().toLowerCase()
  );

  const handleTabRename = (e: any) => {
    if (renameValue.trim() === '') {
      setRenameValue(tabName);
      renameTabComplete(tabName, tabId);
      return;
    }

    if (isDuplicate) {
      setRenameValue(tabName);
      renameTabComplete(tabName, tabId);
      return;
    }

    if (isTooLong) {
      setRenameValue(tabName);
      renameTabComplete(tabName, tabId);
      return;
    }

    if (renameValue.trim().length < 3) {
      setRenameValue(tabName);
      renameTabComplete(tabName, tabId);
      return;
    }

    renameTabComplete(renameValue.trim(), tabId);
  };

  const isEditingThisTab = editingTabId === tabId;

  // For read-only tabs (users), prevent editing
  if (isEditingThisTab && !canPerformActions()) {
    return (
      <div
        style={{
          height: '1.7rem',
          display: 'flex',
          alignItems: 'center',
          paddingTop: 0,
          overflow: 'hidden',
          justifyContent: 'space-between',
          backgroundColor:
            selectedTab === tabId ? selectedTabBackgroundColor : tabAreaBackgroundColor,
          ...(popupClass ? { paddingLeft: '7px', width: '100%' } : {}),
        }}
        className={`${
          selectedTab === tabId ? `commonTab indiItemHighlightTab` : 'commonTab indiItemTab'
        } ${popupClass}`}
      >
        <span
          className="tabText"
          style={{
            color: iconColor,
            textAlign: 'center',
            marginRight: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {tabName.length > 35 ? tabName.substring(0, 35) + '...' : tabName}
          <LockOutlinedIcon style={{ fontSize: '14px', opacity: 0.6 }} />
        </span>
      </div>
    );
  }

  if (isEditingThisTab && canPerformActions()) {
    return (
      <form
        style={{ display: 'inline' }}
        onSubmit={(evt: any) => {
          evt.preventDefault();
          if (!isTooLong) {
            evt.currentTarget.querySelector('input').blur();
          }
        }}
      >
        <Tooltip
          title={
            isDuplicate
              ? 'Tab name already exists'
              : isTooLong
              ? 'Tab name cannot exceed 40 characters'
              : renameValue.length === 40
                ? 'You have reached max limit of tab name'
                : renameValue.trim().length < 3
                  ? 'Tab name must be at least 3 characters long'
                  : renameValue === ''
                    ? 'Tab name cannot be empty'
                    : ''
          }
          placement="right"
          disableHoverListener={!isDuplicate && !isTooLong && renameValue.trim().length >= 3}
          arrow
          componentsProps={{
            tooltip: {
              sx: { fontSize: '12px' },
            },
          }}
        >
          <input
            autoFocus
            value={renameValue}
            onChange={handleTabNameValue}
            className="editTabSelected"
            onBlur={handleTabRename}
            title="Press enter or click away to save"
            style={{
              borderBottom: `2px solid ${isTooLong ? 'red' : '#2bb9bb'}`,
              paddingRight: '2px',
            }}
            maxLength={40}
          />
        </Tooltip>
      </form>
    );
  } else {
    return (
      <div
        style={{
          height: '1.7rem',
          display: 'flex',
          alignItems: 'center',
          paddingTop: 0,
          overflow: 'hidden',
          padding: dashMode === 'Edit' ? '1.5px 5px 0 5px' : '0px 5px 0 5px',
          justifyContent: dashMode === 'Edit' ? 'space-between' : 'center',
          cursor: dashMode !== 'TV Display' ? 'pointer' : 'default',
          backgroundColor:
            selectedTab === tabId ? selectedTabBackgroundColor : tabAreaBackgroundColor,
          ...(popupClass ? { width: '100%', justifyContent: 'start' } : {}),
          opacity: !canPerformActions() || hidden ? 0.7 : 1,
        }}
        className={`${
          selectedTab === tabId ? `commonTab indiItemHighlightTab` : 'commonTab indiItemTab'
        } ${popupClass} ${!canPerformActions() ? 'read-only-tab' : ''}`}
        onClick={(e) => {
          if (dashMode !== 'TV Display') {
            e.stopPropagation();
            if (clickTimer.current) {
              return;
            }
            clickTimer.current = setTimeout(() => {
              selectTab(tabName, tabId);
              clickTimer.current = null;
            }, 200);
          }
        }}
        onDoubleClick={(e) => {
          if (dashMode === 'Edit' && canPerformActions()) {
            e.stopPropagation();
            if (clickTimer.current) {
              clearTimeout(clickTimer.current);
              clickTimer.current = null;
            }
            renameTabBegin(tabId);
          }
        }}
      >
        <span
          className="tabText"
          style={{
            color: iconColor,
            textAlign: 'center',
            marginRight: dashMode !== 'Edit' ? 0 : '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          title={
            hidden
              ? `${tabName} (Hidden)`
              : dashMode === 'Edit'
                ? canPerformActions()
                  ? inPopup
                    ? tabName
                    : `${tabName}\nDouble click to edit name`
                  : `${tabName} (Read-only)`
                : dashMode !== 'TV Display'
                  ? tabName
                  : ''
          }
        >
          {tabName.length > 35 ? tabName.substring(0, 35) + '...' : tabName}
          {/* Show eye-off icon for hidden tabs */}
          {hidden && dashMode === 'Edit' && (
            <VisibilityOffOutlinedIcon style={{ fontSize: '14px', opacity: 0.6 }} />
          )}
          {/* Show lock icon for non-editable tabs */}
          {!canPerformActions() && dashMode === 'Edit' && !hidden && (
            <LockOutlinedIcon style={{ fontSize: '14px', opacity: 0.6 }} />
          )}
        </span>
        {dashMode === 'Edit' && canPerformActions() && !inPopup && (
          <MoreVert
            style={{
              fontSize: '0.75rem',
              transform: 'none',
              color: iconColor,
              ...(popupClass ? { width: '0.75rem', height: '0.75rem' } : {}),
            }}
            className={`closeTab ${inPopup ? 'top-auto' : ''} ${
              inPopup && selectedTab !== tabId ? 'popupClose' : ''
            }
                    ${inPopup && selectedTab === tabId ? 'hidden' : ''}`}
            onClick={handleClick}
          />
        )}

        {/* tab action menu - only for editable tabs */}
        {canPerformActions() && !inPopup && (
          <>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseMenu}
              PaperProps={{
                sx: {
                  backgroundColor: tabAreaBackgroundColor,
                  color: iconColor,
                  mt: 1,
                  boxShadow: `${iconColor === '#ffffff' ? '0px 2px 4px rgba(184, 159, 159, 0.5)' : '0px 2px 4px rgba(0, 0, 0, 0.2)'}`,
                  width: 160,
                },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <MenuItem
                onClick={(e) => {
                  if (dashMode === 'Edit') {
                    e.stopPropagation();
                    if (clickTimer.current) {
                      clearTimeout(clickTimer.current);
                      clickTimer.current = null;
                    }
                    renameTabBegin(tabId);
                  }
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <DriveFileRenameOutlineOutlinedIcon
                    style={{ width: 20, height: 20, color: iconColor }}
                  />
                </ListItemIcon>
                <ListItemText primary="Rename Tab" primaryTypographyProps={{ color: iconColor }} />
              </MenuItem>

              <MenuItem
                disabled={totalTabs === 1}
                selected={activeItem === 'move tab'}
                onClick={(e) => {
                  e.stopPropagation();
                  setSubMenuAnchorEl(e.currentTarget);
                  setActiveItem('move tab');
                }}
              >
                <ListItemIcon>
                  <LuMoveHorizontal style={{ width: 20, height: 20, color: iconColor }} />
                </ListItemIcon>
                <ListItemText primary="Move Tab" primaryTypographyProps={{ color: iconColor }} />
                <ArrowRightIcon style={{ width: 20, height: 20, color: iconColor }} />
              </MenuItem>

              <MenuItem
                disabled={!hidden && visibleTabsCount <= 1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseMenu();
                  toggleTabHidden(tabId);
                }}
              >
                <ListItemIcon>
                  <VisibilityOffOutlinedIcon style={{ width: 20, height: 20, color: iconColor }} />
                </ListItemIcon>
                <ListItemText
                  primary={hidden ? 'Unhide Tab' : 'Hide Tab'}
                  primaryTypographyProps={{ color: iconColor }}
                />
              </MenuItem>

              <MenuItem
                onClick={handleDeleteClick}
                disabled={!hidden && visibleTabsCount <= 1 && totalTabs > 1}
              >
                <ListItemIcon>
                  <DeleteOutlineOutlinedIcon style={{ width: 20, height: 20, color: iconColor }} />
                </ListItemIcon>
                <ListItemText primary="Delete Tab" primaryTypographyProps={{ color: iconColor }} />
              </MenuItem>
            </Menu>

            {/* subMenu For Move Tab */}
            <Menu
              open={openSubMenu}
              anchorEl={subMenuAnchorEl}
              onClose={handleSubmenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  backgroundColor: tabAreaBackgroundColor,
                  color: iconColor,
                  boxShadow: `${iconColor === '#ffffff' ? '0px 2px 4px rgba(184, 159, 159, 0.5)' : '0px 2px 4px rgba(0, 0, 0, 0.2)'}`,
                  width: 160,
                },
              }}
            >
              {getSubMenuOptions(tabIndex, totalTabs)}
            </Menu>
            <DeleteConfirmationDialog
              open={showDeleteConfirmation}
              onClose={handleCancelDelete}
              onConfirm={handleConfirmDelete}
              message={
                <>
                  Do you want to Delete this Tab: <strong>{tabName}</strong>?
                </>
              }
            />
          </>
        )}
      </div>
    );
  }
}

export default IndividualTab;
